import React, { useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setQueueOwner } from '../../redux/slices/musicPlayerSlice';
import { playAllTracks } from '../../utils/playAllTracks';
import { isNative } from '../../utils/webOnly';
import { track as trackEvent } from '../../utils/analytics';
import { entityId } from '../../utils/entityId';
import { useDesignV2 } from '../../utils/designV2';

// The one stateful "Play All" control for track-list surfaces (mix
// tracklists, search results, list modals, Crate Digger recs). Three states:
//
//   idle     ▶ Play All → builds the queue (playAllTracks, or a custom
//                         `onStart` for non-track lists) and claims queueOwner
//   loading  ◌ Play All → spinner from the first millisecond of the click
//                         until the player actually STARTS (the first resolve
//                         can be a lazy YT search + yt-dlp download); the
//                         label stays so the button never reads as emptied
//   owned    ⏸ Pause / ▶ Resume → pauses/resumes IN PLACE — never rebuilds
//                         the queue on a re-click
//
// The pause state is honest by construction: it only shows while this list
// still owns the global queue (queueOwner), and ANY other surface's
// setAudioList/resetAudioList clears that owner. Likes and the playlist
// sidebar implement the same contract with their own skins.
let _ownerSeq = 0;

// List identity derived from CONTENT, never from the surface. A static
// per-surface key collided the moment the surface re-rendered with different
// data: "AI recs for track A" and a later track search both keyed
// "track-list-search", so the second list showed Resume for — and resumed —
// the first list's playback. First/middle/last ids + length disambiguate
// lists cheaply and stay stable across remounts (nav away and back keeps an
// honest Pause state for the SAME list).
const listIdentity = (items) => {
    if (!Array.isArray(items) || !items.length) return null;
    const key = (t) => entityId(t) || t?.musicSrc || t?.link || t?.title || '';
    const mid = items[Math.floor(items.length / 2)];
    return `items:${items.length}:${key(items[0])}:${key(mid)}:${key(items[items.length - 1])}`;
};

const PlayAllButton = ({
    tracks = null,
    onStart = null,          // async () => bool — custom start path (e.g. mixes)
    identityItems = null,    // onStart callers: the list to derive identity from
    ownerId = null,          // explicit identity override (must be list-specific!)
    from,                    // analytics: which surface this button lives on
    className = 'djpal-play-all-btn',
    label = 'Play All',
    iconSize = 10,
    disabled = false,
    title = 'Play all',
}) => {
    const dispatch = useDispatch();
    const designV2 = useDesignV2();
    const labelText = designV2 && label === 'Play All' ? 'Play all' : label;  // DESIGN V2: sentence case
    const queueOwner = useSelector(state => state.musicPlayer.queueOwner);
    const isPlaying = useSelector(state => state.musicPlayer.isPlaying);
    const busyRef = useRef(false);
    const [starting, setStarting] = useState(false);
    // Post-start grace: right after a cold start the native playerState
    // stream (4Hz, isPlaying = intent) briefly dips false while the plugin
    // buffers the fresh queue, which flipped the button Pause → Resume →
    // Pause the moment the spinner cleared — a first-click-only flicker on
    // mobile. Hold Pause through those dips for a beat; a real tap ends the
    // grace immediately so an actual pause is honest.
    const [startGrace, setStartGrace] = useState(false);
    const graceTimerRef = useRef(null);
    React.useEffect(() => () => clearTimeout(graceTimerRef.current), []);
    const mountIdRef = useRef(null);
    if (mountIdRef.current === null) mountIdRef.current = `list:${++_ownerSeq}`;
    const ownerKey = ownerId || listIdentity(tracks || identityItems) || mountIdRef.current;
    const owns = queueOwner?.type === 'list' && queueOwner.id === ownerKey;

    const handleStart = useCallback(async () => {
        if (busyRef.current) return;
        busyRef.current = true;
        setStarting(true);
        trackEvent('play_all_clicked', { from: from || null, action: 'start' });
        // Hold the spinner until the player reports playing — clearing when
        // the queue is merely built flashes the idle label back for the beat
        // before onAudioPlay fires. 20s cap: mixes can extract for a while,
        // and a dead resolve must not spin forever.
        const clearStarting = () => {
            setStarting(false);
            setStartGrace(true);
            clearTimeout(graceTimerRef.current);
            graceTimerRef.current = setTimeout(() => setStartGrace(false), 1600);
            window.removeEventListener('player-track-change', onPlaying);
        };
        const onPlaying = (e) => { if (e.detail?.playing) clearStarting(); };
        let ok = false;
        try {
            ok = onStart ? await onStart() : await playAllTracks(dispatch, tracks || []);
        } catch (e) {
            console.warn('[PlayAllButton] start failed:', e?.message);
        }
        if (!ok) {
            setStarting(false);
            busyRef.current = false;
            return;
        }
        dispatch(setQueueOwner({ type: 'list', id: ownerKey }));
        window.addEventListener('player-track-change', onPlaying);
        setTimeout(clearStarting, 20000);
        busyRef.current = false;
    }, [dispatch, tracks, onStart, ownerKey, from]);

    // Pause/resume in place. Desktop/web: the audio element is the player and
    // jinke's onAudioPlay/onAudioPause sync Redux + broadcast for us. Native:
    // the element is a muted mirror — go through the hijacked jinke
    // play/pause button (the plugin pauses with full bookkeeping, and its
    // playerState stream updates Redux isPlaying + broadcasts the flip).
    const handleToggle = useCallback(() => {
        // A deliberate tap ends the post-start grace — the state it shows
        // from here on must be the real one.
        clearTimeout(graceTimerRef.current);
        setStartGrace(false);
        trackEvent('play_all_clicked', { from: from || null, action: isPlaying ? 'pause' : 'resume' });
        if (isNative) {
            const btn = document.querySelector('.react-jinke-music-player-main .player-content .play-btn');
            if (btn) btn.click();
            return;
        }
        const audio = window.musicPlayerInstance;
        if (!audio) return;
        if (audio.paused) audio.play().catch(() => {});
        else audio.pause();
    }, [from, isPlaying]);

    const showPause = owns && (isPlaying || startGrace);
    return (
        <button
            className={className}
            disabled={disabled}
            onClick={starting ? undefined : owns ? handleToggle : handleStart}
            title={starting ? 'Loading…' : owns ? (showPause ? 'Pause' : 'Resume') : title}
        >
            {starting ? (
                <span className="play-all-spinner" style={{ width: iconSize + 2, height: iconSize + 2 }} />
            ) : showPause ? (
                <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" />
                </svg>
            ) : (
                <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <polygon points="6 3 20 12 6 21 6 3" />
                </svg>
            )}
            {starting ? labelText : owns ? (showPause ? 'Pause' : 'Resume') : labelText}
        </button>
    );
};

export default PlayAllButton;
